import { Map, PolygonArea } from 'components/map';
import { Line } from 'components/map/well';
import { getMapDefaultConfig } from 'components/map/atoms';
import { atomWithStorage } from 'jotai/utils';
import { atom, useAtomValue } from 'jotai';
import { useMemo, useRef } from 'react';
import { createResourceAtom } from 'lib/atoms';
import { formatResource } from 'utils/misc';
import { buildPolygonStyle, findCenter } from 'lib/map';
import { LINE_STYLES } from 'components/map/map-styles';
import {
  DevelopmentAreaResponse,
  WellFeaturesResponse,
  AllocationManagerResponse,
  AllocationManagerLineItem,
} from 'types/api-responses';
import style from './nri-review.module.scss';

interface NriReviewMapProps {
  devAreaId: string; // Required - map won't render without it
  wellId?: string;
  subtractGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  allocationManager?: AllocationManagerResponse;
  selectedTractId?: number | null;
}

export const NriReviewMap = ({
  devAreaId,
  wellId,
  subtractGeometry,
  allocationManager,
  selectedTractId,
}: NriReviewMapProps) => {
  const containerRef = useRef(null);

  // Fetch Development Area data (devAreaId is required)
  const developmentAreaAtom = useMemo(
    () =>
      createResourceAtom<DevelopmentAreaResponse>(
        `/development_areas/${devAreaId}/versions/current`
      ),
    [devAreaId]
  );
  const developmentAreaResource = useAtomValue(developmentAreaAtom);
  const { data: developmentArea, loading: devAreaLoading } = formatResource(
    developmentAreaResource
  );

  // Fetch Well Features data (only if wellId is provided)
  const wellFeaturesAtom = useMemo(() => {
    if (!wellId) {
      return atom<{ loading: false; data: null }>({
        loading: false,
        data: null,
      });
    }
    return createResourceAtom<WellFeaturesResponse>(
      `/wells/${wellId}/versions/current/features`
    );
  }, [wellId]);
  const wellFeaturesResource = useAtomValue(wellFeaturesAtom);
  const { data: wellFeatures, loading: wellFeaturesLoading } =
    formatResource(wellFeaturesResource);

  // Map configuration
  const mapConfigAtom = atomWithStorage(
    'nri-review-map-config',
    getMapDefaultConfig()
  );

  const developmentAreaGeometry = developmentArea?.geometry as
    GeoJSON.Polygon | GeoJSON.MultiPolygon | undefined;

  const wellGeometry = wellFeatures?.survey || wellFeatures?.stick;

  // Extract lineItems from allocation manager response (matching dev area page pattern)
  const allocationTracts: AllocationManagerLineItem[] =
    allocationManager?.lineItems ?? [];

  // Check loading states
  const isLoading = devAreaLoading || (wellId && wellFeaturesLoading);

  if (isLoading || !developmentArea?.geometry) {
    return <div className={style.map}>Loading map...</div>;
  }

  // Calculate map center - use development area geometry directly like allocation-tract-map.tsx line 134
  const { latitude, longitude } = findCenter(developmentArea.geometry);

  return (
    <div
      ref={containerRef}
      className={`${style.mapContainer} ${style.allocationMap}`}
    >
      <Map
        className={style.map}
        longitude={longitude}
        latitude={latitude}
        mapConfigAtom={mapConfigAtom}
        showControls
        showMapFilters
      >
        {/* 1. Development Area - Purple */}
        {developmentAreaGeometry && (
          <PolygonArea
            id="development-area"
            geometry={developmentAreaGeometry}
            style={buildPolygonStyle({
              color: '#A78BFA',
              fillOpacity: 0.3,
              lineOpacity: 0.8,
              lineWidth: 1,
            })}
          />
        )}

        {/* 2. Well Features - Well line */}
        {wellGeometry && (
          <Line
            id="well-features"
            geojson={wellGeometry}
            style={LINE_STYLES.BLACK_SOLID}
          />
        )}

        {/* 3. Allocation Manager Geojson - Orange tract polygons */}
        {allocationTracts && allocationTracts.length > 0 && (
          <>
            {allocationTracts.map(
              (item: AllocationManagerLineItem, index: number) => {
                const tractGeometry = item.unitAllocationTract?.geometry;
                if (!tractGeometry) return null;

                const isSelected =
                  item.unitAllocationTract.id === selectedTractId;
                const style = isSelected
                  ? buildPolygonStyle({
                      color: '#FF8C00',
                      fillOpacity: 0.4,
                      lineOpacity: 1,
                      lineWidth: 4,
                      lineColor: '#0F62FE',
                    })
                  : buildPolygonStyle({
                      color: '#FF8C00',
                      fillOpacity: 0.4,
                      lineOpacity: 0.8,
                      lineWidth: 1,
                    });

                return (
                  <PolygonArea
                    key={`allocation-tract-${index}`}
                    id={`allocation-tract-${index}`}
                    geometry={tractGeometry}
                    style={style}
                  />
                );
              }
            )}
          </>
        )}

        {/* 4. Subtract - Yellow border from createdTract (rendered last to be on top) */}
        {subtractGeometry && (
          <PolygonArea
            id="subtract-border"
            geometry={subtractGeometry}
            style={buildPolygonStyle({
              color: '#FFFF00',
              fillOpacity: 0,
              lineOpacity: 1,
              lineWidth: 2,
            })}
          />
        )}
      </Map>
    </div>
  );
};
